import {useCallback, useContext, useEffect, useState} from 'react';

// import CultureDashboardBenefitsSeriazlizer from '../serializers/CultureDashboardBenefitsSeriazlizer';
import CultureFitSerializers from '../serializers/CultureFitSerializers';
import CultureHouseSerializers from '../serializers/CultureHouseSerializers';
import DreamEmployerSerializers from '../serializers/DreamEmployerSerializers';
import EmpionRecommendationsSerializers from '../serializers/EmpionRecommendationsSerializers';
import FeedbackSerializer from '../serializers/EmployeeFeedbackSerializers';
import RecommendedActionsSerializer from '../serializers/RecommendedActionsSerializers';
import StrengthsSerializer from '../serializers/StrengthsSerializer';
import {Strengths} from '../types/CompanyStrengthsTypes';
import {AllDesiredBenefits, TopBenefits} from '../types/CultureDashboardBenefitsTypes';
import {CultureFitElement} from '../types/CultureFitTypes';
import {CultureHouseElement} from '../types/CultureHouseTypes';
import {CultureSatisfactionElement} from '../types/CultureSatisfactionTypes';
import {DreamEmployerClass, DreamEmployerLegacyClass} from '../types/DreamEmployerTypes';
import {EmpionRecommendation} from '../types/EmpionRecommendationsTypes';
import {EmployeeFeedback} from '../types/EmployeeFeedbackTypes';
import {RecommendedActions} from '../types/RecommendedActionsTypes';

import {CompanyContext} from '../../../../../contexts/CompanyContext/CompanyContext';
import {useApiRead} from '../../../../../hooks/useApiRead';
import {GetRoutes} from '../../../../../lib/api/GET/GetRoutes';
import {CULTURAL_ANALYSIS_STATUS_RELEASED} from '../../../../../lib/constants';
import CultureSatisfactionSerializers from '../../../../../serializers/CultureSatisfactionSerializers';
import {SerializeTo} from '../../../../../utils/serializer/serialize';

interface CulturalAnalysisStatus {
  status: string | null;
  hideCardDetails: boolean;
  pdfUrl?: string;
}
interface CulturalAnalysisResponse {
  culturalAnalysis: CulturalAnalysisStatus;
}

export enum CultureDashboardFilters {
  Strengths = 'strengths',
  TopBenefits = 'top_benefits',
  CultureInformation = 'culture_information',
  DesiredBenefits = 'desired_benefits',
  CultureHouse = 'culture_house',
  CultureSatisfaction = 'culture_satisfaction',
  EmployeeFeedback = 'employee_feedback',
  EmployeeRecommendations = 'employee_recommendations',
  EmpionRecommendations = 'empion_recommendations',
  CultureFit = 'culture_fit',
  DreamEmployerResults = 'dream_employer_results',
}
export const useCulturalDashboardPage = (companyId: string) => {
  const refetchOnWindowFocus = false;
  const [sectionErrors, setSectionErrors] = useState<CultureDashboardFilters[]>([]);

  const serializeStatus = useCallback((data: any): CulturalAnalysisStatus => {
    const serializedData = SerializeTo.CamelCase<CulturalAnalysisResponse>(data);
    return serializedData.culturalAnalysis;
  }, []);

  const {cultureInformation} = useContext(CompanyContext);

  const {
    data: culturalAnalysisStatus,
    isLoading: isCultureAnalysisLoading,
    error: cultureAnalysisError,
  } = useApiRead<CulturalAnalysisStatus>({
    path: GetRoutes.companyCulturalAnalysisStatus(companyId),
    camelCaseOverride: serializeStatus,
    options: {
      refetchOnWindowFocus,
    },
  });

  const {
    data: topStrengths,
    isLoading: isStrengthsLoading,
    error: topStrengthsError,
    refetch: fetchStrengths,
  } = useApiRead<Strengths>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.Strengths, companyId),
    camelCaseOverride: StrengthsSerializer.toObject,
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: topBenefits,
    isLoading: isBenefitsLoading,
    error: topBenefitsError,
    refetch: fetchBenefits,
  } = useApiRead<TopBenefits>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.TopBenefits, companyId),
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: desiredBenefits,
    isLoading: isDesiredBenefitsLoading,
    error: desiredBenefitsError,
    refetch: fetchDesiredBenefits,
  } = useApiRead<AllDesiredBenefits>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.DesiredBenefits, companyId),
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: cultureHouse,
    isLoading: isCultureHouseLoading,
    error: cultureHouseError,
    refetch: fetchCultureHouse,
  } = useApiRead<CultureHouseElement[]>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.CultureHouse, companyId),
    camelCaseOverride: CultureHouseSerializers.toObjects,
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: cultureSatisfaction,
    isLoading: isCultureSatisfactionInformationLoading,
    error: cultureSatisfactionError,
    refetch: fetchCultureSatisfaction,
  } = useApiRead<CultureSatisfactionElement[]>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.CultureSatisfaction, companyId),
    camelCaseOverride: CultureSatisfactionSerializers.toObjects,
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: employeeFeedback,
    isLoading: isEmployeeFeedbackLoading,
    error: employeeFeedbackError,
    refetch: fetchEmployeeFeedback,
  } = useApiRead<EmployeeFeedback>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.EmployeeFeedback, companyId),
    camelCaseOverride: FeedbackSerializer.toObject,
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: cultureFit,
    isLoading: isCultureFitLoading,
    error: cultureFitError,
    refetch: fetchCultureFit,
  } = useApiRead<CultureFitElement[]>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.CultureFit, companyId),
    camelCaseOverride: CultureFitSerializers.toObjects,
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: employeeRecommendedActions,
    isLoading: isEmployeeRecommendedActionsLoading,
    error: employeeRecsError,
    refetch: fetchEmployeeRecommendations,
  } = useApiRead<RecommendedActions>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.EmployeeRecommendations, companyId),
    camelCaseOverride: RecommendedActionsSerializer.toObject,
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: empionRecommendedActions,
    isLoading: isEmpionRecommendedActionsLoading,
    error: empionRecsError,
    refetch: fetchEmpionRecommendations,
  } = useApiRead<EmpionRecommendation[]>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.EmpionRecommendations, companyId),
    camelCaseOverride: EmpionRecommendationsSerializers.toObjects,
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  const {
    data: dreamEmployerResults,
    isLoading: isDreamEmployerResultsLoading,
    error: dreamEmployerError,
    refetch: fetchDreamEmployerResults,
  } = useApiRead<DreamEmployerClass | DreamEmployerLegacyClass>({
    path: GetRoutes.companyCulturalDashboard(CultureDashboardFilters.DreamEmployerResults, companyId),
    camelCaseOverride: DreamEmployerSerializers.toObject,
    options: {
      refetchOnWindowFocus,
      enabled: false,
    },
  });

  useEffect(() => {
    if (culturalAnalysisStatus?.status === CULTURAL_ANALYSIS_STATUS_RELEASED) {
      fetchCultureHouse();
      fetchBenefits();
      fetchCultureSatisfaction();
      // Removing call for now.
      //fetchCultureFit();
      fetchStrengths();
      fetchEmployeeRecommendations();
      fetchEmpionRecommendations();
      fetchEmployeeFeedback();
      fetchDreamEmployerResults();
      fetchDesiredBenefits();
    }
  }, [
    fetchCultureHouse,
    fetchBenefits,
    fetchCultureSatisfaction,
    fetchCultureFit,
    fetchStrengths,
    fetchEmployeeRecommendations,
    fetchEmpionRecommendations,
    fetchEmployeeFeedback,
    fetchDreamEmployerResults,
    fetchDesiredBenefits,
    culturalAnalysisStatus?.status,
  ]);

  useEffect(() => {
    const errors: CultureDashboardFilters[] = [];
    const addError = (filter: CultureDashboardFilters) => {
      errors.push(filter);
    };
    if (desiredBenefitsError) addError(CultureDashboardFilters.DesiredBenefits);
    if (employeeRecsError) addError(CultureDashboardFilters.EmployeeRecommendations);
    if (cultureInformation.error) addError(CultureDashboardFilters.CultureInformation);
    if (topStrengthsError) addError(CultureDashboardFilters.Strengths);
    if (topBenefitsError) addError(CultureDashboardFilters.TopBenefits);
    if (cultureHouseError) addError(CultureDashboardFilters.CultureHouse);
    if (cultureSatisfactionError) addError(CultureDashboardFilters.CultureSatisfaction);
    if (employeeFeedbackError) addError(CultureDashboardFilters.EmployeeFeedback);
    if (cultureFitError) addError(CultureDashboardFilters.CultureFit);
    if (empionRecsError) addError(CultureDashboardFilters.EmpionRecommendations);
    if (dreamEmployerError) addError(CultureDashboardFilters.DreamEmployerResults);
    setSectionErrors(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    topStrengths,
    cultureInformation: cultureInformation.data,
    topBenefits,
    desiredBenefits,
    cultureHouse,
    cultureSatisfaction,
    employeeFeedback,
    employeeRecommendedActions,
    empionRecommendedActions,
    cultureFit,
    dreamEmployerResults,
    sectionErrors,
    culturalAnalysisStatus,
    cultureAnalysisError,
    loading:
      isStrengthsLoading ||
      cultureInformation.isLoading ||
      isBenefitsLoading ||
      isDesiredBenefitsLoading ||
      isCultureHouseLoading ||
      isCultureSatisfactionInformationLoading ||
      isEmployeeFeedbackLoading ||
      isEmployeeRecommendedActionsLoading ||
      isEmpionRecommendedActionsLoading ||
      isCultureFitLoading ||
      isDreamEmployerResultsLoading ||
      isCultureAnalysisLoading,
  };
};
